import { Button, Grid } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import PaymentLinkDetails, {
  PaymentLinkInfo,
  hasPaymentLink,
} from "../../../../../components/PaymentLinkDetails";
import ProfileViewer from "../../../../../components/ProfileViewer";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import PaymentDrawer from "../../../../../components/app/provider/billing/payment/PaymentDrawer";
import { useRoleController } from "../../../../../components/useRoleController";
import { AppointmentSchedulingType } from "../../../../../dummy-data/appointment-list-data-scheduling";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { getStripePromise } from "../../../../../queries/stripe/use-stripe";
import { useUpdateAppointmentStatus } from "../../../../../queries/use-change-appointment-status";
import { useGetAppointment } from "../../../../../queries/use-get-appointment";
import { useGetPatient } from "../../../../../queries/use-get-patient-uuid";
import { setCallData } from "../../../../../redux/feature/join-room/JoinRoomSlice";
import { store } from "../../../../../redux/store";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { getSuccessSnackbar } from "../../../../../utils/utils";
import { VISIT_TYPE_MAPPER } from "./AppointmentScheduling";
import UpsertEditCheckInPatient from "./upsertEditCheckInPatient";
import { getStoredRealm } from "@/utils/auth-storage";

type ExtendedScheduling = AppointmentSchedulingType &
  PaymentLinkInfo & {
    chiefComplaint?: string;
    providerName?: string;
  };

interface CheckInProps {
  disclosureHost: DisclosureHost;
  patientId: string | undefined;
  appointMentData?: ExtendedScheduling | undefined;
  appointmentUuid: string | undefined;
}

const CheckIn = ({
  disclosureHost,
  patientId,
  // appointMentData,
  appointmentUuid,
}: CheckInProps) => {
  const { data, isLoading: loadingAppointment }: any = useGetAppointment({
    appointmentUuid: appointmentUuid,
    providerGroupId: getStoredRealm() ?? "",
  });
  const appointMentData: ExtendedScheduling = data?.data;

  const { mutateAsync: updateAppointmentStatus } = useUpdateAppointmentStatus();
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [payDisc, editDemoDisclosure] = useReduceDisclosure(2);
  const { data: patientData } = useGetPatient(
    patientId,
    !disclosureHost?.opened
  );
  const { hasUpdate } = useRoleController("Check in");

  // const navigateBackHandler = () => {
  //   editDemoDisclosure.open();
  //   localStorage.setItem("appointmentData", JSON.stringify(appointMentData));
  //   navigate(
  //     `/app/provider/patients/edit-patient/${patientId}?fromCheckIn=true`
  //   );
  // };

  function handleStatusUpdate() {
    updateAppointmentStatus({
      appointmentId: appointmentUuid!,
      status: "CHECKED_IN",
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      showSnackbar(getSuccessSnackbar(res));
      if (appointMentData?.mode !== "IN_PERSON") {
        const patientIdParam = appointMentData?.patientId
          ? `&patientid=${appointMentData?.patientId}`
          : "";
        const appointmentIdParam = appointMentData?.uuid
          ? `&appointmentid=${appointMentData?.uuid}`
          : "";

        store.dispatch(
          setCallData({
            patientId: appointMentData?.patientId,
            appointmentId: appointMentData?.uuid,
            patientName: appointMentData?.patientName ?? "",
            providerName: appointMentData?.providerName ?? "",
            startTime: appointMentData?.startTime,
            role: 1,
          })
        );
        navigate(
          `/app/preview?room=${appointMentData?.room}&role=${1}&providerName=${
            appointMentData?.providerName ?? ""
          }&patientName=${appointMentData?.patientName ?? ""}&startTime=${
            appointMentData?.startTime
          }${patientIdParam}${appointmentIdParam}`
        );
      }

      const queryKey = ["appointment-list"];
      client.invalidateQueries({ queryKey });
      client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
      setTimeout(disclosureHost.close, 1500);
    });
  }

  return (
    <SideSheetDrawer
      w="840px"
      open={disclosureHost?.opened}
      title="Check In"
      onClose={() => {
        disclosureHost?.close();
        // Same as the appointment drawer: the emailed Stripe payment can land
        // while this screen is open, so drop the cached appointment on close.
        client.invalidateQueries({ queryKey: ["appointment-list"] });
      }}
      footerActions={
        <>
          <Button onClick={() => editDemoDisclosure.open()}>
            Edit Patient Details
          </Button>
          <Button
            variant="outlined"
            onClick={() => payDisc.open(appointMentData)}
          >
            Collect Payment
          </Button>

          {hasUpdate && (
            <Button variant="contained" onClick={handleStatusUpdate}>
              Complete Check In
            </Button>
          )}
        </>
      }
    >
      <>
        <LoadingOverlay isLoading={loadingAppointment} />
        <UpsertEditCheckInPatient
          disclosureHost={editDemoDisclosure}
          patientData={patientData}
        />
        {/* <UpsertPatientFormDrawer
          patientData={{
            ...patientData,
            patientInsurances: patientData?.patientInsurances?.length
              ? patientData?.patientInsurances
              : [INITIAL_PATIENT_INSURANCE],
            languages: transformMultiSelectFrontEndVal(
              DEFAULT_LANGUAGE_VALUE_LABEL_LIST,
              patientData?.languages
            ),
          }}
          disclosureHost={editDemoDisclosure}
        /> */}

        <Elements stripe={getStripePromise()}>
          <PaymentDrawer
            disclosureHost={payDisc}
            providerId={
              (appointMentData as any)?.providerId
                ? {
                    uuid: (appointMentData as any).providerId,
                    name: appointMentData?.providerName ?? "",
                  }
                : undefined
            }
          />
        </Elements>

        {VISIT_TYPE_MAPPER[
          appointMentData?.mode as AppointmentSchedulingType["mode"]
        ] ?? "-"}
        <Grid container mt={1}>
          <Grid item xs={6}>
            <ProfileViewer
              mt={1}
              firstColW={4.5}
              secondColW={7.5}
              gap={"30px"}
              withoutColn
              data={{
                Appointment_type: appointMentData?.type ?? "",
                Authorization: "",
                "Reason for Visit": appointMentData?.chiefComplaint ?? "",
                ...(hasPaymentLink(appointMentData)
                  ? {
                      "Payment Link Sent": {
                        custom: () => (
                          <PaymentLinkDetails
                            paymentLinkAmount={
                              appointMentData?.paymentLinkAmount
                            }
                            paymentLinkPaid={appointMentData?.paymentLinkPaid}
                          />
                        ),
                      },
                    }
                  : {}),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ProfileViewer
              mt={1}
              gap={"30px"}
              firstColW={4.5}
              secondColW={7.5}
              withoutColn
              data={{
                "Primary Provider Name": appointMentData?.providerName ?? "",
                "Appointment Date & Time": appointMentData?.startTime
                  ? formatDateTime(appointMentData?.startTime)
                  : "-",
              }}
            />
          </Grid>
        </Grid>
      </>
    </SideSheetDrawer>
  );
};

export default CheckIn;
